import { useState } from 'react';
import { handleErr } from 'utils/functions';
import useSnackbar from 'hooks/useSnackbar';
import { DeleteMutation, GenericResource } from 'types/api';

type UseDeleteRecordProps<T extends GenericResource> = {
  useClientDeleteMutation: DeleteMutation<T>;
  recordName: string;
  id: string;
  onSuccess?: () => void;
};

/**
 * useDeleteRecord
 * To be used specifically with Client V2 api & DeleteDialog
 */
const useDeleteRecord = <T extends GenericResource>({
  useClientDeleteMutation,
  recordName,
  id,
  onSuccess,
}: UseDeleteRecordProps<T>) => {
  const [deleteRecord, { isLoading: isLoadingDelete }] =
    useClientDeleteMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { dispatchSuccessSnackbar } = useSnackbar();

  const handleDelete = async () => {
    setSubmitError(null);
    try {
      await deleteRecord({ ids: [id] }).unwrap();
      dispatchSuccessSnackbar(`${recordName} Deleted`);
      if (onSuccess) onSuccess();
    } catch (err) {
      handleErr(err, (errMessage: string) => {
        setSubmitError(errMessage);
      });
    }
  };

  return {
    handleDelete,
    isLoading: isLoadingDelete,
    submitError,
    resetSubmitError: () => setSubmitError(null),
  };
};

export default useDeleteRecord;
